import React, { useEffect, useState } from 'react';
import { Card, Table, Tag, Tooltip, Typography, Space, Select, Spin } from 'antd';
import { CheckCircleTwoTone, CloseCircleTwoTone, ClockCircleTwoTone, MailOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import authService from '../utils/auth';

const { Text } = Typography;
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';
const authHeader = () => ({ Authorization: `Bearer ${authService.getToken()}` });

const FIELD_LABEL = {
  passportName: '护照姓名', passportNumber: '护照号', gender: '性别', nationality: '国籍',
  passportBirthDate: '出生日期', passportIssueDate: '签发日期', passportExpiryDate: '有效期',
  birthPlace: '出生地', passportPhoto: '护照照片',
  documentNumber: '证件号(一日游)', birthDate: '出生日期(一日游)',
  expiryDate: '有效期(一日游)', documentPhoto: '证件照片(一日游)'
};

const fmtVal = (v) => {
  if (v == null || v === '') return '(空)';
  const s = String(v);
  if (s.length > 50) return s.slice(0, 50) + '…';
  // 日期 ISO 简化
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s.slice(0, 10);
  return s;
};

const emailTag = (status) => {
  if (status === 'sent') return <Tag icon={<CheckCircleTwoTone twoToneColor="#52c41a" />} color="success">已发送</Tag>;
  if (status === 'failed') return <Tag icon={<CloseCircleTwoTone twoToneColor="#cf1322" />} color="error">失败</Tag>;
  return <Tag icon={<ClockCircleTwoTone twoToneColor="#faad14" />} color="warning">待发</Tag>;
};

const ChangeTable = ({ fields, oldS, newS }) => (
  <table style={{ borderCollapse: 'collapse', minWidth: 480 }}>
    <thead>
      <tr style={{ background: '#fafafa' }}>
        <th style={{ padding: '4px 8px', border: '1px solid #f0f0f0', textAlign: 'left' }}>字段</th>
        <th style={{ padding: '4px 8px', border: '1px solid #f0f0f0', textAlign: 'left' }}>原值</th>
        <th style={{ padding: '4px 8px', border: '1px solid #f0f0f0', textAlign: 'left' }}>新值</th>
      </tr>
    </thead>
    <tbody>
      {(fields || []).map(f => (
        <tr key={f}>
          <td style={{ padding: '4px 8px', border: '1px solid #f0f0f0' }}>{FIELD_LABEL[f] || f}</td>
          <td style={{ padding: '4px 8px', border: '1px solid #f0f0f0', color: '#999' }}>{fmtVal(oldS?.[f])}</td>
          <td style={{ padding: '4px 8px', border: '1px solid #f0f0f0', color: '#1677ff', fontWeight: 600 }}>{fmtVal(newS?.[f])}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

export default function PassportUpdateLogs() {
  const [data, setData] = useState({ total: 0, items: [] });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [days, setDays] = useState(90);
  const [emailStatus, setEmailStatus] = useState(undefined);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: pageSize, offset: (page - 1) * pageSize, days };
      if (emailStatus) params.emailStatus = emailStatus;
      const { data: resp } = await axios.get(`${API_BASE}/admin/passport-update-logs`, {
        headers: authHeader(), params
      });
      setData(resp?.data || { total: 0, items: [] });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, pageSize, days, emailStatus]);

  const columns = [
    {
      title: '时间', dataIndex: 'createdAt', width: 150,
      render: t => dayjs(t).format('YYYY-MM-DD HH:mm')
    },
    {
      title: '团', dataIndex: 'tour', width: 200,
      render: t => t ? (
        <div>
          <div>{t.productName}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            出发 {t.departureDate ? dayjs(t.departureDate).format('YYYY-MM-DD') : '-'}
          </Text>
        </div>
      ) : '-'
    },
    {
      title: '游客', dataIndex: 'tourist', width: 180,
      render: t => t ? (
        <div>
          <div>{t.passportName || t.touristName}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>EKOK {t.ekok || '-'} · 销售 {t.salesName || '-'}</Text>
        </div>
      ) : '-'
    },
    {
      title: '操作人', dataIndex: 'updatedByName', width: 120,
      render: (v, r) => v ? <span>{v} <Tag color={r.updatedByRole === 'admin' ? 'red' : 'blue'}>{r.updatedByRole}</Tag></span> : '系统'
    },
    {
      title: '变更', key: 'changes',
      render: r => (
        <Space size={[4, 4]} wrap>
          {(r.changedFields || []).map(f => (
            <Tooltip key={f} title={
              <div style={{ minWidth: 240 }}>
                <div>原：<span style={{ color: '#ccc' }}>{fmtVal(r.oldSnapshot?.[f])}</span></div>
                <div>新：<span style={{ color: '#7cf' }}>{fmtVal(r.newSnapshot?.[f])}</span></div>
              </div>
            }>
              <Tag color="processing">{FIELD_LABEL[f] || f}</Tag>
            </Tooltip>
          ))}
        </Space>
      )
    },
    {
      title: <span><MailOutlined /> 邮件</span>, dataIndex: 'emailStatus', width: 100,
      render: (s, r) => (
        <Tooltip title={
          s === 'sent' ? `${r.emailSentAt ? dayjs(r.emailSentAt).format('MM-DD HH:mm') : ''} → ${r.emailRecipients || ''}` :
          s === 'failed' ? r.emailError : '排队中'
        }>{emailTag(s)}</Tooltip>
      )
    },
    { title: '备注', dataIndex: 'remark', ellipsis: true }
  ];

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>护照更新记录</h2>
        <Space>
          <span style={{ color: '#999' }}>仅展示「已结团」团上发生的护照字段变更</span>
          <Select value={days} onChange={v => { setPage(1); setDays(v); }} style={{ width: 110 }}
            options={[{ value: 7, label: '最近 7 天' }, { value: 30, label: '最近 30 天' }, { value: 90, label: '最近 90 天' }, { value: 365, label: '最近 1 年' }]} />
          <Select value={emailStatus} placeholder="邮件状态" allowClear onChange={v => { setPage(1); setEmailStatus(v); }} style={{ width: 130 }}
            options={[{ value: 'sent', label: '已发送' }, { value: 'failed', label: '失败' }, { value: 'pending', label: '待发' }]} />
        </Space>
      </div>
      <Card>
        <Spin spinning={loading}>
          <Table
            rowKey="id"
            size="small"
            columns={columns}
            dataSource={data.items}
            expandable={{
              expandedRowRender: (r) => <ChangeTable fields={r.changedFields} oldS={r.oldSnapshot} newS={r.newSnapshot} />,
              rowExpandable: r => (r.changedFields || []).length > 0
            }}
            pagination={{
              current: page, pageSize, total: data.total,
              showSizeChanger: true, pageSizeOptions: [10, 20, 50, 100],
              showTotal: t => `共 ${t} 条`,
              onChange: (p, ps) => { setPage(p); setPageSize(ps); }
            }}
          />
        </Spin>
      </Card>
    </div>
  );
}
